import { readNativeGitGenerationContext } from '@crowdex/host-kit/renderer/native';
import { RadioGroup, RadioGroupItem } from '@crowdex/ui/components/radio-group';
import type { NativeGitGenerationContext } from '@crowdex/workspace-protocol';
import { useEffect, useEffectEvent, useState } from 'react';

/** What a commit takes: the conversation's own files, or everything the folder changed. */
export type CommitScope = 'conversation' | 'folder';

/** The files a conversation's turns changed: its own, and those another may have changed too. */
export type ConversationCommitFiles = { own: readonly string[]; shared: readonly string[] };

const LISTED = 6;

/**
 * The scope of a commit from a conversation and what it takes, read again as the scope changes.
 * Without the conversation's files (outside a conversation), the folder is the only scope.
 */
export function useCommitScope({
  workspace,
  conversationFiles,
  active,
  read = readNativeGitGenerationContext,
}: {
  workspace: string | null;
  conversationFiles: ConversationCommitFiles | null | undefined;
  /** Its dialog is open: the scope is read then, and starts at the conversation again. */
  active: boolean;
  /** Reads what a commit of the scope would take. */
  read?: (workspace: string, paths?: readonly string[]) => Promise<NativeGitGenerationContext>;
}) {
  const scoped = conversationFiles !== null && conversationFiles !== undefined;
  const [scope, setScope] = useState<CommitScope>(scoped ? 'conversation' : 'folder');
  const [preview, setPreview] = useState<{ files: string[]; folderFileCount: number } | null>(null);
  const paths = scope === 'conversation' && scoped ? conversationFiles.own : undefined;
  const pathsKey = paths?.join('\0');
  // `pathsKey` stands for `paths`: the scope is read again when its files change, not when the
  // list is a new array of the same files.
  const readScope = useEffectEvent((workspace: string, key: string | undefined) =>
    read(workspace, key === undefined ? undefined : paths),
  );
  useEffect(() => {
    if (active) setScope(scoped ? 'conversation' : 'folder');
  }, [active, scoped]);
  useEffect(() => {
    if (!active || workspace === null) return;
    let current = true;
    setPreview(null);
    // Unread, the choice stays without its list: the commit itself still says what it took.
    void readScope(workspace, pathsKey)
      .then((context) => {
        if (current)
          setPreview({ files: context.commitFiles, folderFileCount: context.folderFileCount });
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [active, workspace, pathsKey]);
  return { scope, setScope, scoped, paths, preview, shared: conversationFiles?.shared ?? [] };
}

export type CommitScopeState = ReturnType<typeof useCommitScope>;

/**
 * The choice of what a commit takes, with the files it takes listed: the conversation's own changes
 * (the default), or all the folder's, another conversation's half-done work included.
 */
export function CommitScopeChoice({ state }: { state: CommitScopeState }) {
  const { scope, setScope, scoped, preview, shared } = state;
  const files = preview?.files ?? [];
  const listed = files.slice(0, LISTED);
  return (
    <div className="grid gap-2 text-sm" data-commit-scope>
      {scoped && (
        <RadioGroup
          aria-label="What to commit"
          onValueChange={(value) => setScope(value === 'folder' ? 'folder' : 'conversation')}
          value={scope}
        >
          <RadioGroupItem value="conversation">
            <span className="min-w-0">
              <span className="block font-medium text-foreground text-sm">
                This conversation's changes
              </span>
              <span className="block text-muted-foreground text-xs">
                The files its turns changed
                {shared.length > 0
                  ? `, without ${shared.length} another conversation may have changed too`
                  : ''}
              </span>
            </span>
          </RadioGroupItem>
          <RadioGroupItem value="folder">
            <span className="min-w-0">
              <span className="block font-medium text-foreground text-sm">
                All changes in the folder
                {preview !== null && scope === 'conversation'
                  ? ` (${preview.folderFileCount} ${preview.folderFileCount === 1 ? 'file' : 'files'})`
                  : ''}
              </span>
              <span className="block text-muted-foreground text-xs">
                Other conversations' work in progress included
              </span>
            </span>
          </RadioGroupItem>
        </RadioGroup>
      )}
      {preview === null ? (
        <p className="text-muted-foreground text-xs">Reading the changes…</p>
      ) : files.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {scope === 'conversation'
            ? 'This conversation has no changes left to commit.'
            : 'Nothing to commit.'}
        </p>
      ) : (
        <div className="grid gap-0.5" data-commit-files>
          <p className="text-muted-foreground text-xs">
            {files.length} {files.length === 1 ? 'file' : 'files'} will be committed
          </p>
          {listed.map((file) => (
            <span className="truncate font-mono text-foreground/80 text-xs" key={file} title={file}>
              {file}
            </span>
          ))}
          {files.length > listed.length && (
            <span className="text-muted-foreground text-xs">
              +{files.length - listed.length} more
            </span>
          )}
        </div>
      )}
    </div>
  );
}
